import { WorkExperienceCompany } from '@applicator/shared'
import React from 'react'
import { createPortal } from 'react-dom'

import DatePicker from '../DatePicker/DatePicker'

interface WorkExperiencePortalsProps {
  companies: WorkExperienceCompany[]
  datePickerState: {
    companyId: string
    roleId: string
    field: 'startDate' | 'endDate'
    position: { top: number; left: number; width: number }
    triggerKey: string
  } | null
  datePickerRef: React.RefObject<HTMLDivElement>
  editingRoleStartDate: string
  editingRoleEndDate: string
  openTypeDropdown: { companyId: string; roleId: string } | null
  typeDropdownRefs: React.MutableRefObject<{ [key: string]: HTMLElement | null }>
  employmentTypes: string[]
  onDateChange: (date: string) => void
  onDatePickerClose: () => void
  onUpdateEmploymentType: (companyId: string, roleId: string, employmentType: string) => void
}

export default function WorkExperiencePortals({
  companies,
  datePickerState,
  datePickerRef,
  editingRoleStartDate,
  editingRoleEndDate,
  openTypeDropdown,
  typeDropdownRefs,
  employmentTypes,
  onDateChange,
  onDatePickerClose,
  onUpdateEmploymentType,
}: WorkExperiencePortalsProps) {
  return (
    <>
      {/* Date Picker Portal */}
      {datePickerState && (() => {
        const roleExists = companies
          .find((c) => c.id === datePickerState.companyId)
          ?.roles?.some((r) => r.id === datePickerState.roleId)
        if (!roleExists) return null

        const currentValue =
          datePickerState.field === 'startDate' ? editingRoleStartDate : editingRoleEndDate

        return createPortal(
          <DatePicker
            ref={datePickerRef}
            value={currentValue}
            onChange={onDateChange}
            onClose={onDatePickerClose}
            position={datePickerState.position}
            includeTime={false}
            monthYearOnly={true}
            allowPresent={datePickerState.field === 'endDate'}
          />,
          document.body
        )
      })()}

      {/* Type Dropdown Portal */}
      {openTypeDropdown && (() => {
        const triggerKey = `${openTypeDropdown.companyId}-${openTypeDropdown.roleId}-type`
        const triggerElement = typeDropdownRefs.current[triggerKey]
        if (!triggerElement) return null

        const rect = triggerElement.getBoundingClientRect()
        const role = companies
          .find((c) => c.id === openTypeDropdown.companyId)
          ?.roles?.find((r) => r.id === openTypeDropdown.roleId)
        if (!role) return null

        return createPortal(
          <div
            role="presentation"
            className="fixed bg-surface-default border border-border-default rounded-md shadow-lg z-50 min-w-[180px]"
            style={{
              top: `${rect.bottom + 4}px`,
              left: `${rect.left}px`,
              zIndex: 999999,
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {employmentTypes.map((type) => {
              const isSelected = role.employmentType === type
              return (
                <div
                  key={type}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.stopPropagation()
                    onUpdateEmploymentType(openTypeDropdown.companyId, openTypeDropdown.roleId, type)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      onUpdateEmploymentType(openTypeDropdown.companyId, openTypeDropdown.roleId, type)
                    }
                  }}
                  className={`px-4 py-2 text-sm cursor-pointer hover:bg-surface-muted first:rounded-t-md last:rounded-b-md flex items-center gap-2 ${
                    isSelected
                      ? 'bg-brand-primary-subtle text-text-primary'
                      : 'text-text-secondary'
                  }`}
                >
                  {isSelected && (
                    <svg className="w-4 h-4 text-interactive" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span>{type}</span>
                </div>
              )
            })}
          </div>,
          document.body
        )
      })()}
    </>
  )
}
